import React, { useState } from 'react'

import { getValueFormat } from '@baurine/grafana-value-formats'
import { Select, Table } from 'antd'
import { ColumnsType, TableProps } from 'antd/lib/table'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'

interface Log {
  component: string
  level: string
  name: string

  t1Count: number
  t2Count: number
  abs_diff: number // count, aka. delta
  diff: number // percentage
}

interface LogTableProps {
  data: Log[]
}

export const LogTable: React.FC<LogTableProps> = ({ data }) => {
  const {
    selections,
    updateSelectedOption,
    filteredData,
    levelFilter,
    componentFilter
  } = useFilter(data)
  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({})
  const [sortedInfo, setSortedInfo] = useState<SorterResult<Log>>({})
  const [page, setPage] = useState(1)
  const handleChange: TableProps<Log>['onChange'] = (_, filters, sorter) => {
    setFilteredInfo(filters)
    setSortedInfo(sorter as SorterResult<Log>)
  }
  const columns: ColumnsType<Log> = [
    {
      title: 'Component',
      dataIndex: 'component',
      key: 'component',
      filters: componentFilter,
      onFilter: (value, record) => record.component.includes(value as string),
      filteredValue: filteredInfo.component || null
    },
    {
      title: 'Log Content',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Level',
      dataIndex: 'level',
      key: 'level',
      filters: levelFilter,
      onFilter: (value, record) => record.level.includes(value as string),
      filteredValue: filteredInfo.level || null
    },
    {
      title: 'T1 Count',
      dataIndex: 't1Count',
      key: 't1Count',
      sorter: (a, b) => a.t1Count - b.t1Count,
      sortOrder: sortedInfo.columnKey === 't1Count' ? sortedInfo.order : null
    },
    {
      title: 'T2 Count',
      dataIndex: 't2Count',
      key: 't2Count',
      sorter: (a, b) => a.t2Count - b.t2Count,
      sortOrder: sortedInfo.columnKey === 't2Count' ? sortedInfo.order : null
    },
    {
      title: 'Delta',
      dataIndex: 'abs_diff',
      key: 'abs_diff',
      sorter: (a, b) => a.abs_diff - b.abs_diff,
      sortOrder: sortedInfo.columnKey === 'abs_diff' ? sortedInfo.order : null
    },
    {
      title: 'Diff',
      dataIndex: 'diff',
      key: 'diff',
      sorter: (a, b) => a.diff - b.diff,
      sortOrder: sortedInfo.columnKey === 'diff' ? sortedInfo.order : null,
      render: (value: number) => {
        return value >= 1e10 || value <= -1e10 ? (
          <span>Infinity</span>
        ) : (
          <span>{getValueFormat('percentunit')(value, 2)}</span>
        )
      }
    }
  ]

  return (
    <>
      <div className='mb-4'>
        Category:{' '}
        <Select
          defaultValue='all'
          style={{ width: 150 }}
          onChange={updateSelectedOption}
          options={selections}
        />
      </div>
      <Table
        columns={columns}
        dataSource={filteredData}
        onChange={handleChange}
        pagination={{
          position: ['bottomRight'],
          current: page,
          defaultPageSize: 50,
          onChange: (p) => setPage(p)
        }}
      />
    </>
  )
}

const selections = [
  {
    value: 'all',
    label: 'All'
  },
  {
    value: 'fail',
    label: `"fail" category`
  },
  {
    value: 'mutation',
    label: '0 to N category'
  }
]
const useFilter = (data: Log[]) => {
  const [selectedOption, setSelectedOption] = useState('all')
  const updateSelectedOption = (v: string) => {
    setSelectedOption(v)
  }
  const filteredData = filterMap[selectedOption](data)
  const _levelFilter: { [k: string]: string } = {}
  const _componentFilter: { [k: string]: string } = {}
  data.forEach((d) => {
    if (!_levelFilter[d.level]) {
      _levelFilter[d.level] = d.level
    }
    if (!_componentFilter[d.component]) {
      _componentFilter[d.component] = d.component
    }
  })

  const levelFilter = Object.values(_levelFilter).map((v) => ({
    text: v,
    value: v
  }))
  const componentFilter = Object.values(_componentFilter).map((v) => ({
    text: v,
    value: v
  }))

  return {
    selections,
    selectedOption,
    updateSelectedOption,
    filteredData,
    levelFilter,
    componentFilter
  }
}

const filterMap: { [k: string]: (d: Log[]) => Log[] } = {
  all: (data) => data,
  fail: (data) => data.filter((d) => d.name.includes('fail')),
  mutation: (data) => data.filter((d) => d.t1Count === 0 && d.t2Count > 0)
}
